import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart, PatientDoc } from '@usacsmideo/types';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';

// import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
// import { getNextPage } from '../../../../data/navigation/getNextPage';
// import { NavForward } from '../../../../components/buttons/NavForward';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { formatPhoneE164, validatePhone } from '../../../../utils/phone';
import { FieldInfo } from '../../../../components/FieldInfo';
import { FormSelect } from '../../../../components/forms/FormSelect';

import * as Sentry from '@sentry/browser';
import { formatDateForCalendar } from '../../../../utilities/formatDateForCalendar';
import { formatDateForStorage } from '../../../../utilities/formatDateForStorage';
import { formatDateForDisplay } from '../../../../utilities/formatDateForDisplay';

import { validateAdultDob } from '../../../../utilities/validateAdultDob';

import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';

export const Route = createFileRoute('/_authenticated/$patientId/info/patientInfo')({
  component: PatientInfoPage,
});

function PatientInfoPage() {
  const { mutate } = useUpdateChartSection();
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;
  // const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  // const [prevMarked, setPrevMarked] = React.useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
  const [alerted, setAlerted] = React.useState(false);
  const [hasGender, setHasGender] = React.useState(false);

  // REFS TO PREVENT RACE CONDITIONS DURING CHAINED MUTATIONS
  const isSubmittingRef = React.useRef(false);
  const capturedFormValuesRef = React.useRef<typeof patientInfoForm.state.values | null>(null);

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // INITIALIZE FORM
  const patientInfoForm = useForm({
    defaultValues: {
      firstName: chart?.PATIENT_INFO_1?.firstName ? chart.PATIENT_INFO_1?.firstName : '',
      middleInitial: chart?.PATIENT_INFO_1?.middleInitial ? chart.PATIENT_INFO_1?.middleInitial : '',
      lastName: chart?.PATIENT_INFO_1?.lastName ? chart.PATIENT_INFO_1?.lastName : '',
      gender: chart?.PATIENT_INFO_1?.gender ? chart.PATIENT_INFO_1.gender : '',
      DOB: chart?.PATIENT_INFO_1?.DOB ? formatDateForCalendar(chart.PATIENT_INFO_1?.DOB) : '',
      selfReporting: chart?.PATIENT_INFO_1?.selfReporting === false ? 'false' : 'true', // Default to true if not set
      // PHONE FIELDS: READ NEW FIELDS WITH FALLBACK TO LEGACY
      primaryPhone: chart?.PATIENT_INFO_2?.primaryPhone
        ? chart.PATIENT_INFO_2?.primaryPhone
        : chart?.PATIENT_INFO_2?.cellPhone
          ? chart.PATIENT_INFO_2?.cellPhone
          : '',
      secondaryPhone: chart?.PATIENT_INFO_2?.secondaryPhone
        ? chart.PATIENT_INFO_2?.secondaryPhone
        : chart?.PATIENT_INFO_2?.homePhone
          ? chart.PATIENT_INFO_2?.homePhone
          : '',
      email: chart?.PATIENT_INFO_2?.email
        ? chart.PATIENT_INFO_2?.email
        : patientInfo?.contactEmail
          ? patientInfo.contactEmail
          : '',
      city: chart?.PATIENT_INFO_2?.city ? chart.PATIENT_INFO_2?.city : '',
      state: chart?.PATIENT_INFO_2?.state ? chart.PATIENT_INFO_2?.state : '',
      ZIP: chart?.PATIENT_INFO_2?.ZIP ? chart.PATIENT_INFO_2?.ZIP : '',
      country: chart?.PATIENT_INFO_2?.country ? chart.PATIENT_INFO_2?.country : 'USA',
      streetAddress: chart?.PATIENT_INFO_2?.streetAddress ? chart.PATIENT_INFO_2?.streetAddress : '',
      organDonor: chart?.LIVING_WILL?.organDonor || '',
    },
    onSubmit: () => {
      handleIdInfoUpdate();
    },
  });

  // SET INITIAL FOCUS ON CELL PHONE INPUT
  React.useEffect(() => {
    const cellPhoneInput = document.getElementById('cell-phone-input');
    if (cellPhoneInput) {
      cellPhoneInput.focus();
    }
  });

  // SET PATIENT HAS GENDER
  React.useEffect(() => {
    if (chart?.PATIENT_INFO_1?.gender?.length > 0) {
      setHasGender(true);
    }
  }, [chart]);

  // RESET FORM VALUES WHEN CHART DATA ARRIVES
  // THE useForm HOOK ONLY EVALUATES defaultValues ONCE ON MOUNT,
  // BUT chart IS INITIALLY UNDEFINED UNTIL THE SNAPSHOT ARRIVES
  // SKIP RESET IF WE'RE IN THE MIDDLE OF A MUTATION CHAIN TO PREVENT RACE CONDITIONS
  React.useEffect(() => {
    if (isSubmittingRef.current) {
      // Skip form reset while mutations are in progress
      return;
    }
    if (chart?.PATIENT_INFO_1 || chart?.PATIENT_INFO_2) {
      const info1 = chart.PATIENT_INFO_1;
      const info2 = chart.PATIENT_INFO_2;
      if (info1) {
        patientInfoForm.setFieldValue('firstName', info1.firstName || '');
        patientInfoForm.setFieldValue('middleInitial', info1.middleInitial || '');
        patientInfoForm.setFieldValue('lastName', info1.lastName || '');
        patientInfoForm.setFieldValue('gender', info1.gender || '');
        patientInfoForm.setFieldValue('DOB', info1.DOB ? formatDateForCalendar(info1.DOB) : '');
        patientInfoForm.setFieldValue('selfReporting', info1.selfReporting === false ? 'false' : 'true');
      }
      if (info2) {
        // PHONE FIELDS: READ NEW FIELDS WITH FALLBACK TO LEGACY
        patientInfoForm.setFieldValue('primaryPhone', info2.primaryPhone || info2.cellPhone || '');
        patientInfoForm.setFieldValue('secondaryPhone', info2.secondaryPhone || info2.homePhone || '');
        patientInfoForm.setFieldValue('email', info2.email || patientInfo?.contactEmail || '');
        patientInfoForm.setFieldValue('city', info2.city || '');
        patientInfoForm.setFieldValue('state', info2.state || '');
        patientInfoForm.setFieldValue('ZIP', info2.ZIP || '');
        patientInfoForm.setFieldValue('country', info2.country || 'USA');
        patientInfoForm.setFieldValue('streetAddress', info2.streetAddress || '');
      }
    }
    if (chart?.LIVING_WILL) {
      patientInfoForm.setFieldValue('organDonor', chart.LIVING_WILL.organDonor || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart?.PATIENT_INFO_1, chart?.PATIENT_INFO_2, chart?.LIVING_WILL]);

  // ALERT PATIENT TO CURRENT STATE OF SCANNED DL INFO
  React.useEffect(() => {
    const dlHasInfo = () => {
      if (!chart || !chart.PATIENT_INFO_1 || !chart.PATIENT_INFO_2) {
        return false;
      } else if (
        chart.PATIENT_INFO_1.firstName?.length > 0 ||
        (chart.PATIENT_INFO_1.middleInitial && chart.PATIENT_INFO_1.middleInitial?.length > 0) ||
        chart.PATIENT_INFO_1.lastName?.length > 0 ||
        chart.PATIENT_INFO_1.DOB?.toString().length > 0 ||
        chart.PATIENT_INFO_2.streetAddress.length > 0 ||
        chart.PATIENT_INFO_2.city.length > 0 ||
        chart.PATIENT_INFO_2.state.length > 0 ||
        chart.PATIENT_INFO_2.ZIP.length > 0
      ) {
        return true;
      } else {
        return false;
      }
    };

    if (chart?.PHOTO_ID?.photoIdentificationFrontURI && dlHasInfo() && !alerted) {
      setAlerted(true);
      setTimeout(() => {
        alert(`We were able to read the following information from your driver's license:
        Name: ${chart.PATIENT_INFO_1.firstName ? chart.PATIENT_INFO_1.firstName : ''} ${chart.PATIENT_INFO_1.middleInitial ? chart.PATIENT_INFO_1.middleInitial : ''} ${chart.PATIENT_INFO_1.lastName ? chart.PATIENT_INFO_1.lastName : ''}
        DOB: ${chart.PATIENT_INFO_1.DOB.seconds ? formatDateForDisplay(chart.PATIENT_INFO_1.DOB) : ''}
        Address: ${chart.PATIENT_INFO_2.streetAddress}
        City: ${chart.PATIENT_INFO_2.city}
        State: ${chart.PATIENT_INFO_2.state}
        Zip: ${chart.PATIENT_INFO_2.ZIP}
        If any of this information is incorrect or incomplete, please correct it on this page.`);
      }, 2000);
    }
  }, [chart, alerted]);

  // // MARK PREVIOUS PAGE AS COMPLETED
  // React.useEffect(() => {
  //   if (patientInfo && patientInfo.navObj && !prevMarked) {
  //     markPreviousPageCompleted({
  //       currentPageName: 'patientInfo',
  //       patientInfo,
  //       value: true,
  //     });
  //     setPrevMarked(true);
  //   }
  // }, [patientInfo, prevMarked]);

  // // GET NEXT PAGE URL
  // React.useEffect(() => {
  //   if (chart && patientInfo?.navObj) {
  //     (async () => {
  //       const awaitedUrl = await getNextPage({
  //         currentPage: 'patientInfo',
  //         chart: chart,
  //         patientInfo: patientInfo,
  //       });
  //       setNextPageURL(awaitedUrl);
  //     })();
  //   }
  // }, [chart, patientInfo]);

  // WRITE FORM DATA TO FIRESTORE
  // NOTE: UPDATES RUN SEQUENTIALLY (INFO_1 -> displayName -> INFO_2) TO ENSURE
  // onSuccess CALLBACKS FIRE CORRECTLY. PARALLEL MUTATIONS CAUSE CALLBACK OVERWRITES.
  const handleIdInfoUpdate = () => {
    // CAPTURE FORM VALUES AT SUBMIT TIME TO PREVENT RACE CONDITIONS
    isSubmittingRef.current = true;
    capturedFormValuesRef.current = { ...patientInfoForm.state.values };
    handleIdInfo1Update();
  };
  const handleIdInfo1Update = () => {
    const captured = capturedFormValuesRef.current;
    if (!captured) {
      isSubmittingRef.current = false;
      return;
    }

    mutate(
      {
        patientId: patientId,
        docData: {
          PATIENT_INFO_1: {
            firstName: captured.firstName,
            middleInitial: captured.middleInitial,
            lastName: captured.lastName,
            gender: captured.gender,
            DOB: captured.DOB
              ? formatDateForStorage(captured.DOB)
              : formatDateForStorage(new Date().toLocaleDateString('en-US')),
            selfReporting: captured.selfReporting === 'true' ? true : false, // Convert string to boolean
          },
        },
      },
      {
        onSuccess: () => {
          const captured = capturedFormValuesRef.current;
          if (!captured) {
            isSubmittingRef.current = false;
            return;
          }
          // SYNC displayName TO USER DOC FOR ADMIN CONSOLE SEARCH
          const firstName = captured.firstName?.trim() || '';
          const lastName = captured.lastName?.trim() || '';
          const displayName = `${firstName} ${lastName}`.trim();
          if (displayName) {
            mutatePatientDoc(
              { patientId, displayName },
              {
                onSuccess: () => {
                  // displayName synced, now update INFO_2
                  handleIdInfo2Update();
                },
                onError: (error) => {
                  Sentry.captureException(error);
                  console.error('Error updating displayName:', error);
                  // CONTINUE WITH INFO_2 EVEN IF displayName FAILS
                  handleIdInfo2Update();
                },
              },
            );
          } else {
            handleIdInfo2Update();
          }
        },
        onError: (error) => {
          isSubmittingRef.current = false;
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  const handleIdInfo2Update = () => {
    const captured = capturedFormValuesRef.current;
    if (!captured) {
      isSubmittingRef.current = false;
      return;
    }

    // NORMALIZE PHONE NUMBERS TO E.164 FORMAT BEFORE SAVING
    const primaryPhoneE164 = formatPhoneE164(captured.primaryPhone);
    const secondaryPhoneE164 = formatPhoneE164(captured.secondaryPhone);

    mutate(
      {
        patientId: patientId,
        docData: {
          PATIENT_INFO_2: {
            // NEW NORMALIZED FIELDS
            primaryPhone: primaryPhoneE164 || captured.primaryPhone,
            secondaryPhone: secondaryPhoneE164 || captured.secondaryPhone,
            // LEGACY FIELDS (FOR BACKWARD COMPATIBILITY)
            cellPhone: primaryPhoneE164 || captured.primaryPhone,
            homePhone: secondaryPhoneE164 || captured.secondaryPhone,
            country: captured.country,
            email: captured.email,
            city: captured.city,
            state: captured.state,
            ZIP: captured.ZIP,
            streetAddress: captured.streetAddress,
          },
        },
      },
      {
        onSuccess: () => {
          handleLivingWillUpdate();
        },
        onError: (error) => {
          isSubmittingRef.current = false;
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  const handleLivingWillUpdate = () => {
    const captured = capturedFormValuesRef.current;
    if (!captured) {
      isSubmittingRef.current = false;
      return;
    }

    mutate(
      {
        patientId: patientId,
        docData: {
          LIVING_WILL: {
            livingWill: chart?.LIVING_WILL?.livingWill ?? false,
            organDonor: captured.organDonor,
          },
        },
      },
      {
        onSuccess: () => {
          isSubmittingRef.current = false;
          setIsSubmitted(true);
        },
        onError: (error) => {
          isSubmittingRef.current = false;
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`);
          console.error(error);
        },
      },
    );
  };

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId) {
    return (
      <>
        <div id="photo-upload-page" className="flex flex-col items-center">
          <h1 className="place-self-center text-3xl font-bold">PATIENT INFORMATION</h1>
          <div className="my-4">
            <div id="front-id-container" className="flex flex-col items-center">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  patientInfoForm.handleSubmit();
                }}
              >
                <div className="flex flex-col">
                  <patientInfoForm.Field
                    name="selfReporting"
                    children={(field) => (
                      <div className="w-1/2">
                        <FormSelect
                          id="self-reporting-select"
                          label="Who is filling out this form?*"
                          name="selfReporting"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          options={[
                            { name: 'The patient', value: 'true' },
                            { name: "The patient's legally appointed health care agent.", value: 'false' },
                          ]}
                          required={true}
                          value={field.state.value}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </div>
                    )}
                  />

                  <patientInfoForm.Field
                    name="firstName"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'First name cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="First Name*"
                          id="first-name"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="middleInitial"
                    children={(field) => (
                      <>
                        <FormInput
                          label="Middle Initial"
                          id="middle-initial"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="lastName"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Last name cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Last Name*"
                          id="last-name"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="gender"
                    listeners={{
                      onChange: ({ value }) => {
                        if (value && value.length > 0) {
                          setHasGender(true);
                        }
                      },
                      onMount: ({ value }) => {
                        if (value && value.length > 0) {
                          setHasGender(true);
                        }
                      },
                    }}
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Please select a gender above';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <div className="w-1/3">
                        <FormSelect
                          label="Gender*"
                          name="gender"
                          id="gender-select"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                          options={[
                            { name: '--Please choose an option--', value: '' },
                            { name: 'Male', value: 'male' },
                            { name: 'Female', value: 'female' },
                            { name: 'Non-Binary', value: 'non-binary' },
                          ]}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </div>
                    )}
                  />
                  <patientInfoForm.Field
                    name="DOB"
                    validators={{
                      onChange: ({ value }) => validateAdultDob(value),
                    }}
                    children={(field) => (
                      <div className="scheme-dark">
                        <FormInput
                          label="DOB* (MM/DD/YYYY)"
                          id="dob"
                          type="date"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <p className="text-xl font-bold text-green-900 italic">
                          *Please confirm patient DOB matches government ID before submitting.
                        </p>
                        <FieldInfo fieldMeta={field.state.meta} />
                      </div>
                    )}
                  />
                  <div className="my-2">
                    <h2 className="text-xl font-bold">Primary Phone*</h2>
                    <patientInfoForm.Field
                      name="primaryPhone"
                      validators={{
                        onChange: ({ value }) => {
                          if (!value) return 'Primary Phone is required';
                          const result = validatePhone(value);
                          return result.isValid ? undefined : result.error;
                        },
                      }}
                      children={(field) => (
                        <>
                          <PhoneInput
                            international
                            defaultCountry="US"
                            placeholder="Enter phone number"
                            value={field.state.value}
                            onChange={(value: string | undefined) => field.handleChange(value || '')}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                  </div>
                  <div className="my-2">
                    <h2 className="text-xl font-bold">Secondary Phone (Optional)</h2>
                    <patientInfoForm.Field
                      name="secondaryPhone"
                      children={(field) => (
                        <>
                          <PhoneInput
                            international
                            defaultCountry="US"
                            placeholder="Enter phone number"
                            value={field.state.value}
                            onChange={(value: string | undefined) => field.handleChange(value || '')}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                  </div>

                  <patientInfoForm.Field
                    name="email"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Email cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Email*"
                          id="email"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="streetAddress"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Street address cannot be blank.';
                        } else if (value.length < 5) {
                          return 'Street address must be at least 5 characters';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Street Address*"
                          id="street-address"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="city"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'City cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="City*"
                          id="city"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="state"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'State cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="State*"
                          id="state"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="ZIP"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'ZIP Code cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="ZIP*"
                          id="ZIP"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="country"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Country cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Country*"
                          id="country"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="organDonor"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Please select an organ donor status';
                        }
                        return undefined;
                      },
                    }}
                    children={(field) => (
                      <div className="w-1/2">
                        <FormSelect
                          id="organ-donor-select"
                          label="Organ Donor Status*"
                          name="organDonor"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          options={[
                            { name: 'Choose an option', value: '' },
                            { name: 'I am an organ donor', value: 'Yes' },
                            { name: 'I am not an organ donor', value: 'No' },
                            { name: 'To be decided', value: 'TBD' },
                          ]}
                          required={true}
                          value={field.state.value}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </div>
                    )}
                  />
                </div>
                <patientInfoForm.Subscribe
                  selector={(state) => [state.canSubmit, state.isSubmitting]}
                  children={([canSubmit, isSubmitting]) => (
                    <>
                      {!canSubmit && (
                        <div className="mt-4 flex flex-col items-center">
                          <p className="text-red-500">Please correct any errors above before re-submitting.</p>
                        </div>
                      )}
                      <div className="mt-8 flex flex-col items-center">
                        <Button
                          id="submit-id-info"
                          type="submit"
                          textContent="Submit"
                          disabled={!hasGender || !canSubmit || isSubmitting}
                          onClick={handleIdInfoUpdate}
                          submitted={isSubmitted}
                        />
                      </div>
                    </>
                  )}
                />
              </form>
            </div>
          </div>
          <div id="nav" className="my-4 flex flex-col items-center">
            {isSubmitted && (
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={!isSubmitted}
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
